import { Row, Col, Card, Table, Button, Modal } from "antd";

import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";

// Refactored imports
import { useRobotManagement } from "../hooks/useRobotManagement";
import { organizationsService } from "../services/organizationsService";
import { robotsService } from "../services/robotsService";
import { OrganizationColorManager, BUTTON_STYLES } from "../constants";
import { createRobotColumns } from "../components/tableColumns";
import { AddRobotModal, EditRobotModal } from "../components/RobotModals";

const MyRobotsPage = () => {
  const { isAuthenticated, authFetch, loading: authLoading } = useAuth();
  const { notifySuccess, notifyError } = useNotification();

  // Use custom hook for robot management
  const {
    robotsData,
    robotTypes,
    loadRobots,
    loadRobotTypes,
    createRobot,
    updateRobot,
    deleteRobot,
    pagination,
  } = useRobotManagement(true); // true = my robots only

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRobot, setSelectedRobot] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [newRobot, setNewRobot] = useState({
    robot_id: "",
    name: "",
    type_id: "",
    organizations: [],
    password: "",
    iconFile: null,
    icon_path: null,
    iconType: "preset",
  });
  const [editingRobot, setEditingRobot] = useState({
    robot_id: "",
    name: "",
    type_id: "",
    icon_path: null,
    organizations: [],
    iconFile: null,
    iconType: "preset",
  });
  const [organizationColorManager] = useState(
    () => new OrganizationColorManager(),
  );
  const [organizationsList, setOrganizationsList] = useState([]);

  const robotsTableSource = robotsData.results || [];

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadRobots();
    }
  }, [authLoading, isAuthenticated, loadRobots]);

  const handleEditButtonClicked = (robotRecord) => {
    setSelectedRobot(robotRecord);

    setEditingRobot({
      robot_id: robotRecord.robot_id,
      name: robotRecord.name,
      type_id: robotRecord.robot_type?.type_id,
      organizations: robotRecord.organizations?.map((org) => org.org_id) || [],
      password: "",
      api_key: robotRecord.api_key || "",
      icon_path: robotRecord.icon_path,
      iconFile: null,
      iconType: "preset",
    });

    setIsModalVisible(true);
  };

  const handleEditModalOk = async () => {
    const editingData = { ...editingRobot };

    delete editingData.api_key; // Do not send api_key in the update request
    delete editingData.iconFile; // Don't send iconFile
    delete editingData.iconType; // Don't send iconType

    if (editingData.password === "") {
      delete editingData.password;
    }

    // comparing the editingRobot.organizations with the selectedRobot.organizations
    if (
      JSON.stringify(editingData.organizations) ===
      JSON.stringify(selectedRobot.organizations?.map((org) => org.org_id))
    ) {
      delete editingData.organizations;
    }

    if (!selectedRobot.robot_type?.type_id && !editingData.type_id) {
      delete editingData.type_id;
    } else if (editingData.type_id === selectedRobot.robot_type?.type_id) {
      delete editingData.type_id;
    }

    if (editingData.name === selectedRobot.name) {
      delete editingData.name;
    }
    if (editingData.robot_id === selectedRobot.robot_id) {
      delete editingData.robot_id;
    }

    if (
      !editingData?.icon_path ||
      editingData?.icon_path === selectedRobot?.icon_path
    ) {
      delete editingData.icon_path;
    }

    const hasIconUpload = editingRobot.iconFile !== null;
    const hasDataChanges = Object.keys(editingData).length > 0;

    if (!hasDataChanges && !hasIconUpload) {
      notifyError("No changes made to update.");
      setIsModalVisible(false);
      return;
    }

    try {
      // Update robot data if there are changes
      if (hasDataChanges) {
        await updateRobot(selectedRobot.robot_id, editingData);
      }

      // Upload icon if user selected one
      if (hasIconUpload) {
        await robotsService.uploadIcon(
          authFetch,
          selectedRobot.robot_id,
          editingRobot.iconFile,
        );
      }

      setIsModalVisible(false);
      notifySuccess("Robot updated successfully.");
      loadRobots(); // Reload robots to show updated icon
    } catch (error) {
      // Error handled in hook or logged here
      console.error("Error updating robot:", error);
    }
  };

  const handleEditModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddButtonClick = () => {
    setIsAddModalVisible(true);
  };

  const handleAddModalOk = async () => {
    try {
      const robotData = { ...newRobot };
      delete robotData.iconFile; // Don't send iconFile in create request
      delete robotData.iconType; // Don't send iconType in create request

      const createdRobot = await createRobot(robotData);

      // Upload icon if user selected one
      if (newRobot.iconFile && createdRobot) {
        await robotsService.uploadIcon(
          authFetch,
          robotData.robot_id,
          newRobot.iconFile,
        );
      }

      setIsAddModalVisible(false);
      setNewRobot({
        robot_id: "",
        name: "",
        type_id: "",
        organizations: [],
        password: "",
        iconFile: null,
        icon_path: null,
        iconType: "preset",
      });
      notifySuccess("Robot created successfully.");
      loadRobots(); // Reload robots to show new robot with icon
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
  };

  const handleDeleteButtonClicked = (robotId) => {
    Modal.confirm({
      title: `Are you sure you want to delete this robot "${robotId}"?`,
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        await deleteRobot(robotId);
      },
    });
  };

  // Create table columns using shared factory
  const columns = createRobotColumns(organizationColorManager, (key) => key, {
    showOwner: false,
    showActions: true,
    onEdit: handleEditButtonClicked,
    onDelete: handleDeleteButtonClicked,
  });

  useEffect(() => {
    const fetchAdminOrganizations = async () => {
      // Load organizations for the user (admin role only)
      try {
        const response = await organizationsService.getMyOrganizationsRoles(
          authFetch,
          1,
          100,
        );
        const orgsData = response.data;
        const orgsWithRoles = orgsData?.results || [];
        // Filter out organizations that do not have a role of 'admin'
        const orgsWithAdminRole = orgsWithRoles
          .filter((record) => record.roles.includes("admin"))
          .map((record) => record.organization);
        setOrganizationsList(orgsWithAdminRole || []);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    if (!authLoading && isAuthenticated && authFetch) {
      loadRobotTypes();
      fetchAdminOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, authFetch]);

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title="Manage My Robots"
              extra={
                <>
                  {/* <Radio.Group onChange={onChange} defaultValue="a">
                    <Radio.Button value="a">All</Radio.Button>
                    <Radio.Button value="b">ONLINE</Radio.Button>
                  </Radio.Group> */}
                  <Button
                    icon={<PlusOutlined />}
                    style={BUTTON_STYLES.add}
                    onClick={handleAddButtonClick}
                  >
                    New Robot
                  </Button>
                </>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={robotsTableSource}
                  pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ["5", "10", "20", "50"],
                  }}
                  className="ant-border-space"
                  rowKey="robot_id"
                  onRow={(record) => ({
                    onDoubleClick: () => handleEditButtonClicked(record),
                  })}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      {/* Edit robot modal */}
      <EditRobotModal
        visible={isModalVisible}
        robot={editingRobot}
        robotTypes={robotTypes}
        organizations={organizationsList}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        onChange={(updates) => setEditingRobot({ ...editingRobot, ...updates })}
        showOwner={false}
      />

      {/* Add robot modal */}
      <AddRobotModal
        visible={isAddModalVisible}
        robot={newRobot}
        robotTypes={robotTypes}
        organizations={organizationsList}
        onOk={handleAddModalOk}
        onCancel={handleAddModalCancel}
        onChange={(updates) => setNewRobot({ ...newRobot, ...updates })}
      />
    </>
  );
};

export default MyRobotsPage;
